// InitialAssessment.jsx
import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Button,
  CircularProgress,
} from "@mui/material";
import { useMutation, useQuery } from "react-query";
import { QK } from "../../../base/qk";
import {
  fetchInitialAssessment,
  getSubCategory,
  postCheckBox,
} from "../../../api/services/form";
import { useFormId } from "../../../api/services/customHooks";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import CustomerDetails from "./CustomerDetails";
import CustomCheckbox from "../../CustomForm/CustomCheckbox";

const InitialAssessment = () => {
  const [selectedValues, setSelectedValues] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const { id: clientId } = useParams();

  const { data: formId } = useFormId("Initial Assessment");
  const idOfForm = formId?.form_id;

  const { data: subForms, isLoading: subFormsLoading } = useQuery(
    [QK.subCategory, clientId],
    () => getSubCategory(idOfForm),
    {
      enabled: !!idOfForm,
    }
  );

  const form = "Initial_Assessment";

  const { data, isLoading: assessmentLoading } = useQuery(
    ["AllInitialAssessment", idOfForm, clientId, form],
    () => fetchInitialAssessment(idOfForm, clientId, form),
    {
      enabled: !!idOfForm && !!clientId && !!form,
    }
  );

  const postMutation = useMutation(postCheckBox, {
    onSuccess: () => {
      enqueueSnackbar("Checkbox added successfully", { variant: "success" });
      setSelectedValues({});
    },
    onError: () => {
      enqueueSnackbar("Failed to add checkbox", { variant: "error" });
    },
  });

  const handleCheckboxChange = (formFieldId, checked) => {
    setSelectedValues((prev) => ({
      ...prev,
      [formFieldId]: { checked: !!checked },
    }));
  };

  const handlePost = () => {
    const checkboxData = Object.keys(selectedValues).map((formField) => ({
      form_field: parseInt(formField),
      client: clientId,
      checked: !!selectedValues[formField].checked,
    }));

    if (checkboxData.length > 0) {
      postMutation.mutate(checkboxData);
    } else {
      enqueueSnackbar("No checkboxes selected", { variant: "warning" });
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const isLoading = subFormsLoading || assessmentLoading;

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: "1px solid #e2e8f0",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              py: 2,
              px: 3,
              color: "#64748b",
              "&.Mui-selected": {
                color: "#1976d2",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#1976d2",
              height: 3,
            },
          }}
        >
          <Tab label="Initial Assessment" />
          <Tab label="Customer Details" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <>
              {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Grid container spacing={3}>
                    {subForms?.map((form) => {
                      const matchedData = data?.[form.sub_form_name] || [];
                      return (
                        <Grid item xs={12} md={6} key={form.id}>
                          <Box sx={{ p: 1 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: "#1976d2",
                                fontWeight: 600,
                                mb: 2,
                              }}
                            >
                              {form.sub_form_name}
                            </Typography>
                            <Box component="ul" sx={{ p: 0, m: 0, listStyle: "none" }}>
                              {matchedData?.map((item, index) => (
                                <Box
                                  component="li"
                                  key={index}
                                  sx={{
                                    py: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    cursor: "default",
                                  }}
                                >
                                  <CustomCheckbox
                                    defaultColor="#ff4d4d"
                                    isInitiallyChecked={item.checked_value || false}
                                    onClick={(isChecked) =>
                                      handleCheckboxChange(item.id, isChecked)
                                    }
                                  />
                                  <Typography variant="body2">{item.form_name}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handlePost}
                      disabled={postMutation.isLoading}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: 4,
                        py: 1,
                        borderRadius: "8px",
                      }}
                    >
                      {postMutation.isLoading ? "Submitting..." : "Submit"}
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}

          {activeTab === 1 && <CustomerDetails />}
        </Box>
      </Paper>
    </Box>
  );
};

export default InitialAssessment;